import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Calendar,
  DollarSign,
  MessageSquare,
  Phone,
  Mail,
  FileText,
  Plus,
  Edit2,
  Archive,
  Clock,
  User,
  Briefcase,
  Tag,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  TrendingUp,
  CheckCircle2,
  Users,
  Layers,
} from 'lucide-react';
import {
  useOpportunity,
  useUpdateOpportunity,
  useAddOpportunityActivity,
  useDeleteOpportunity,
  OpportunityActivity,
  SCOPING_STAGES,
  RagStatus,
} from '@/hooks/useOpportunities';
import { useHasPermission } from '@/hooks/useAuth';
import { useRateCardTypes } from '@/hooks/useRateCards';
import { useWorkstreams } from '@/hooks/useWorkstreams';
import { AddProjectToOpportunity } from '@/components/pipeline/AddProjectToOpportunity';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { UserPicker } from '@/components/common/UserPicker';
import { cn } from '@/lib/utils';

export function OpportunityDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canEdit = useHasPermission('pipeline.opportunities.update');
  const canDelete = useHasPermission('pipeline.opportunities.delete');

  const { data, isLoading, refetch } = useOpportunity(id!);
  const addActivity = useAddOpportunityActivity();
  const deleteOpportunity = useDeleteOpportunity();

  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        Opportunity not found
      </div>
    );
  }

  const { opportunity, activities } = data;
  const projects = opportunity.projects || [];
  const servicesValue = opportunity.servicesValue || 0;
  const scopedValue = opportunity.scopedValue || 0;
  const unscopedValue = opportunity.unscopedValue || Math.max(0, servicesValue - scopedValue);
  const totalDays = opportunity.totalDays || 0;
  const percentScoped = servicesValue > 0 ? Math.round((scopedValue / servicesValue) * 100) : 0;

  // Collect red risks
  const redRisks: Array<{ label: string; note: string | null }> = [];
  if (opportunity.customerRelationshipStatus === 'red') {
    redRisks.push({ label: 'Customer Relationship', note: opportunity.customerRelationshipNotes });
  }
  if (opportunity.scopingStatus === 'red') {
    redRisks.push({ label: 'Scoping', note: opportunity.scopingNotes });
  }
  if (opportunity.customerReadinessStatus === 'red') {
    redRisks.push({ label: 'Customer Readiness', note: opportunity.customerReadinessNotes });
  }
  if (opportunity.deliveryStatus === 'red') {
    redRisks.push({ label: 'Delivery', note: opportunity.deliveryNotes });
  }
  for (const project of projects) {
    if (project.healthStatus === 'red') {
      redRisks.push({ label: `${project.name} — Project Health`, note: project.healthNotes || null });
    }
  }

  const handleDelete = async () => {
    if (!id) return;
    if (
      !window.confirm(
        `Are you sure you want to delete the opportunity "${opportunity.title}"? This will remove it from the pipeline.`
      )
    ) {
      return;
    }
    try {
      await deleteOpportunity.mutateAsync(id);
      navigate('/opportunities');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete opportunity';
      window.alert(message);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-200 pb-4 mb-4 md:mb-6">
        <div className="flex items-start md:items-center gap-3 md:gap-4">
          <button
            onClick={() => navigate('/opportunities')}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 transition-colors touch-target flex-shrink-0"
            title="Back to Pipeline"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3">
              {opportunity.customerId ? (
                <Link
                  to={`/customers/${opportunity.customerId}`}
                  className="font-heading text-lg md:text-2xl font-bold text-xtravirt-dark hover:text-xtravirt-cyan transition-colors truncate"
                >
                  {opportunity.customerName}
                </Link>
              ) : (
                <h1 className="font-heading text-lg md:text-2xl font-bold text-xtravirt-dark truncate">
                  {opportunity.customerName}
                </h1>
              )}
              <span className="hidden md:inline text-gray-400">—</span>
              <span className="text-sm md:text-xl text-gray-600 truncate">{opportunity.title}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 md:gap-4 text-sm">
              <span
                className={cn(
                  'rounded-full px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-sm font-medium',
                  opportunity.engagementStatus === 'in_delivery'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-blue-100 text-blue-700'
                )}
              >
                {opportunity.engagementStatus === 'in_delivery' ? 'In Delivery' : 'Pipeline'}
              </span>
              {opportunity.servicesValue && (
                <span className="flex items-center gap-1 text-green-700 font-semibold text-xs md:text-sm">
                  <DollarSign className="h-3 w-3 md:h-4 md:w-4" />
                  {formatCurrency(opportunity.servicesValue, opportunity.currency)}
                </span>
              )}
            </div>
          </div>
        </div>

        {(canEdit || canDelete) && (
          <div className="flex items-center gap-2 ml-11 md:ml-0">
            {canEdit && (
              <button
                onClick={() => setShowEditModal(true)}
                className="btn-secondary text-sm"
              >
                <Edit2 className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Edit</span>
              </button>
            )}
            {canDelete && (
              <button
                onClick={handleDelete}
                disabled={deleteOpportunity.isPending}
                className="btn-secondary text-red-600 hover:bg-red-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Archive className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">
                  {deleteOpportunity.isPending ? 'Deleting…' : 'Delete'}
                </span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Risk Register (when any red status) */}
        {redRisks.length > 0 && (
          <div className="card p-4 md:p-6 mb-4 md:mb-6 border-l-4 border-l-red-500">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-heading text-base md:text-lg font-semibold text-red-800 mb-2">
                  Risks &amp; Blockers
                </h3>
                <ul className="space-y-2">
                  {redRisks.map((risk, i) => (
                    <li key={i} className="text-sm">
                      <span className="font-medium text-red-900">{risk.label}</span>
                      {risk.note && <span className="text-red-700"> — {risk.note}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Main Column (2/3 on desktop) */}
          <div className="lg:col-span-2 space-y-4 md:space-y-6">
            {/* Value Breakdown Card */}
            <div className="card p-4 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="h-5 w-5 text-xtravirt-cyan" />
                <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">
                  Value Breakdown
                </h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Total Value</span>
                  <span className="font-semibold text-xtravirt-dark text-base">
                    {formatCurrency(servicesValue, opportunity.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1 text-gray-500">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    Scoped
                  </span>
                  <span className="font-semibold text-green-700">
                    {formatCurrency(scopedValue, opportunity.currency)}
                    <span className="text-xs text-gray-500 ml-1">({percentScoped}%)</span>
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1 text-gray-500">
                    <Clock className="h-3.5 w-3.5 text-amber-600" />
                    Unscoped
                  </span>
                  <span className="font-semibold text-amber-600">
                    {formatCurrency(unscopedValue, opportunity.currency)}
                    <span className="text-xs text-gray-500 ml-1">({100 - percentScoped}%)</span>
                  </span>
                </div>
                {/* Progress bar */}
                <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden mt-3">
                  <div
                    className="absolute left-0 top-0 h-full bg-green-500 rounded-full transition-all"
                    style={{ width: `${percentScoped}%` }}
                  />
                </div>
                {totalDays > 0 && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 pt-1">
                    <Calendar className="h-3 w-3" />
                    {(totalDays / 100).toFixed(1)} days scoped across {projects.length} {projects.length === 1 ? 'project' : 'projects'}
                  </div>
                )}
              </div>
            </div>

            {/* Projects List */}
            <div className="card p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">
                    Projects
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 mt-1">
                    {projects.length} project{projects.length !== 1 ? 's' : ''} &middot;{' '}
                    {formatCurrency(scopedValue, opportunity.currency)} total scoped
                  </p>
                </div>
                <button
                  onClick={() => setShowAddProjectModal(true)}
                  className="btn-primary text-sm w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Project
                </button>
              </div>

              {projects.length > 0 ? (
                <div className="space-y-3">
                  {projects.map((project) => {
                    const stageInfo = SCOPING_STAGES.find((s) => s.id === project.currentScopingStage);
                    return (
                      <Link
                        key={project.id}
                        to={`/projects/${project.id}`}
                        className="block p-4 rounded-lg border border-gray-200 hover:border-xtravirt-cyan hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-xtravirt-cyan/10 flex-shrink-0">
                              <FileText className="h-5 w-5 text-xtravirt-cyan" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-xtravirt-dark truncate">
                                  {project.name}
                                </h4>
                                <RagDot status={project.healthStatus || 'na'} />
                              </div>
                              <div className="mt-1 flex items-center gap-2 flex-wrap">
                                {stageInfo && (
                                  <span
                                    className={cn(
                                      'inline-block px-2 py-0.5 rounded text-xs font-medium',
                                      stageInfo.color
                                    )}
                                  >
                                    {stageInfo.name}
                                  </span>
                                )}
                                <span
                                  className={cn(
                                    'inline-block px-2 py-0.5 rounded text-xs font-medium',
                                    project.status === 'approved'
                                      ? 'bg-green-100 text-green-700'
                                      : project.status === 'draft'
                                      ? 'bg-gray-100 text-gray-600'
                                      : 'bg-yellow-100 text-yellow-700'
                                  )}
                                >
                                  {project.status}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="text-right">
                              <p className="text-sm font-semibold text-xtravirt-dark">
                                {(project.totalDays / 100).toFixed(1)} days
                              </p>
                              {project.totalCost > 0 && (
                                <p className="text-xs text-green-700 font-medium">
                                  {formatCurrency(project.totalCost, project.currency || opportunity.currency)}
                                </p>
                              )}
                            </div>
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>

                        {/* Stage progress indicator */}
                        <div className="mt-3 flex gap-1">
                          {SCOPING_STAGES.map((stage, i) => {
                            const stageIndex = SCOPING_STAGES.findIndex(
                              (s) => s.id === project.currentScopingStage
                            );
                            const isCompleted = i < stageIndex;
                            const isCurrent = stage.id === project.currentScopingStage;

                            return (
                              <div
                                key={stage.id}
                                className={cn(
                                  'h-1.5 flex-1 rounded-full',
                                  isCompleted
                                    ? 'bg-green-500'
                                    : isCurrent
                                    ? 'bg-xtravirt-cyan'
                                    : 'bg-gray-200'
                                )}
                                title={stage.name}
                              />
                            );
                          })}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-sm">No projects yet</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Add a project to start scoping this opportunity
                  </p>
                  <button
                    onClick={() => setShowAddProjectModal(true)}
                    className="btn-secondary mt-4"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add First Project
                  </button>
                </div>
              )}
            </div>

            {/* Workstream Coverage Card */}
            <WorkstreamCoverageCard
              coverage={opportunity.workstreamCoverage || []}
            />

            {/* Description (if exists) */}
            {opportunity.description && (
              <div className="card p-4 md:p-6">
                <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-2 md:mb-3">
                  Description
                </h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                  {opportunity.description}
                </p>
              </div>
            )}

            {/* Next Steps (if exists) */}
            {opportunity.nextStepsActions && (
              <div className="card p-4 md:p-6">
                <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-2 md:mb-3">
                  Next Steps
                </h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                  {opportunity.nextStepsActions}
                </p>
              </div>
            )}
          </div>

          {/* Side Column (1/3 on desktop) */}
          <div className="space-y-4 md:space-y-6">
            {/* Health Status Card */}
            <div className="card p-4 md:p-6">
              <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-3 md:mb-4">
                Health Status
              </h3>
              <div className="space-y-3">
                <RagRow
                  label="Customer Relationship"
                  status={opportunity.customerRelationshipStatus || 'na'}
                  note={opportunity.customerRelationshipNotes}
                />
                <RagRow
                  label="Scoping"
                  status={opportunity.scopingStatus || 'na'}
                  note={opportunity.scopingNotes}
                />
                <RagRow
                  label="Customer Readiness"
                  status={opportunity.customerReadinessStatus || 'na'}
                  note={opportunity.customerReadinessNotes}
                />
                <RagRow
                  label="Delivery"
                  status={opportunity.deliveryStatus || 'na'}
                  note={opportunity.deliveryNotes}
                />
              </div>
            </div>

            {/* Team Card */}
            <div className="card p-4 md:p-6">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <Users className="h-5 w-5 text-xtravirt-cyan" />
                <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">
                  Team
                </h3>
              </div>
              <dl className="space-y-3">
                <DetailItem
                  icon={User}
                  label="Account Director"
                  value={opportunity.accountDirectorName || '—'}
                />
                <DetailItem
                  icon={User}
                  label="Scoping Lead"
                  value={opportunity.scopingLeadName || '—'}
                />
                <DetailItem
                  icon={User}
                  label="Delivery Lead"
                  value={opportunity.deliveryLeadName || '—'}
                />
              </dl>
            </div>

            {/* Details Card */}
            <div className="card p-4 md:p-6">
              <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-3 md:mb-4">
                Details
              </h3>
              <dl className="space-y-3">
                <DetailItem
                  icon={Tag}
                  label="Funding"
                  value={opportunity.fundingTypeName || opportunity.funding || 'TBC'}
                  badge={opportunity.fundingTypeName || opportunity.funding}
                />
                <DetailItem
                  icon={Briefcase}
                  label="Service Class"
                  value={opportunity.serviceClass || '—'}
                />
                <DetailItem
                  icon={Building2}
                  label="Sales Org"
                  value={opportunity.salesOrg || '—'}
                />
                {opportunity.contractStartDate && (
                  <DetailItem
                    icon={Calendar}
                    label="Contract Start"
                    value={new Date(opportunity.contractStartDate).toLocaleDateString()}
                  />
                )}
                {opportunity.servicesExpiryDate && (
                  <DetailItem
                    icon={Clock}
                    label="Expiry"
                    value={new Date(opportunity.servicesExpiryDate).toLocaleDateString()}
                  />
                )}
                {/* Audit Trail */}
                <div className="pt-3 mt-3 border-t border-gray-100 space-y-2">
                  <div className="text-xs text-gray-500">
                    Created {new Date(opportunity.createdAt).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-gray-500">
                    Updated {new Date(opportunity.updatedAt).toLocaleDateString()}
                  </div>
                </div>
              </dl>
            </div>

            {/* Activity Log */}
            <div className="card p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">
                  Activity Log
                </h3>
                <button
                  onClick={() => setShowActivityForm(!showActivityForm)}
                  className="btn-secondary text-xs md:text-sm py-1.5 px-2 md:px-3"
                >
                  <Plus className="h-3 w-3 md:mr-1" />
                  <span className="hidden md:inline">Add Note</span>
                </button>
              </div>

              {showActivityForm && (
                <ActivityForm
                  opportunityId={id!}
                  onSubmit={(data) => {
                    addActivity.mutate({ opportunityId: id!, ...data });
                    setShowActivityForm(false);
                  }}
                  onCancel={() => setShowActivityForm(false)}
                  isPending={addActivity.isPending}
                />
              )}

              <div className="space-y-4 max-h-96 overflow-y-auto">
                {activities.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No activity yet
                  </p>
                ) : (
                  activities.map((activity: OpportunityActivity) => (
                    <ActivityItem key={activity.id} activity={activity} />
                  ))
                )}
              </div>
            </div>

            {/* Notes Card (if exists) */}
            {opportunity.notes && (
              <div className="card p-4 md:p-6">
                <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark mb-2 md:mb-3">
                  Notes
                </h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                  {opportunity.notes}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Project Modal */}
      {showAddProjectModal && (
        <AddProjectToOpportunity
          opportunityId={id!}
          opportunityTitle={opportunity.title}
          onClose={() => setShowAddProjectModal(false)}
          onSuccess={() => refetch()}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <EditOpportunityModal
          opportunity={opportunity}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => refetch()}
        />
      )}
    </div>
  );
}

// ============ Detail Item ============

interface DetailItemProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  badge?: string | null;
}

function DetailItem({ icon: Icon, label, value, badge }: DetailItemProps) {
  const FUNDING_BADGES: Record<string, string> = {
    'Broadcom T6 Entitlement': 'bg-blue-100 text-blue-700',
    'Broadcom T1-T4 Cash': 'bg-green-100 text-green-700',
    'Xtravirt Direct': 'bg-purple-100 text-purple-700',
    // Legacy fallbacks
    Entitlement: 'bg-blue-100 text-blue-700',
    Cash: 'bg-green-100 text-green-700',
    POC: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <dt className="text-xs text-gray-500">{label}</dt>
        <dd className="text-sm font-medium text-gray-900 mt-0.5">
          {badge && FUNDING_BADGES[badge] ? (
            <span className={cn('inline-block px-2 py-0.5 rounded text-xs font-medium', FUNDING_BADGES[badge])}>
              {value}
            </span>
          ) : (
            value
          )}
        </dd>
      </div>
    </div>
  );
}

// ============ Activity Form ============

interface ActivityFormProps {
  opportunityId: string;
  onSubmit: (data: { type: 'call' | 'meeting' | 'email' | 'note'; description: string }) => void;
  onCancel: () => void;
  isPending: boolean;
}

function ActivityForm({ onSubmit, onCancel, isPending }: ActivityFormProps) {
  const [type, setType] = useState<'call' | 'meeting' | 'email' | 'note'>('note');
  const [description, setDescription] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (description.trim()) {
      onSubmit({ type, description });
    }
  };

  const ACTIVITY_TYPES = [
    { id: 'note' as const, label: 'Note', icon: FileText },
    { id: 'call' as const, label: 'Call', icon: Phone },
    { id: 'meeting' as const, label: 'Meeting', icon: MessageSquare },
    { id: 'email' as const, label: 'Email', icon: Mail },
  ];

  return (
    <form onSubmit={handleSubmit} className="border-b border-gray-200 pb-4 mb-4">
      <div className="flex gap-1 mb-3">
        {ACTIVITY_TYPES.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setType(t.id)}
              className={cn(
                'flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                type === t.id
                  ? 'bg-xtravirt-cyan text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              <Icon className="h-3 w-3" />
              {t.label}
            </button>
          );
        })}
      </div>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Add a note..."
        rows={3}
        className="input w-full text-sm"
        autoFocus
      />
      <div className="flex justify-end gap-2 mt-3">
        <button type="button" onClick={onCancel} className="btn-secondary text-xs py-1.5 px-3">
          Cancel
        </button>
        <button
          type="submit"
          disabled={!description.trim() || isPending}
          className="btn-primary text-xs py-1.5 px-3"
        >
          {isPending ? 'Adding...' : 'Add Activity'}
        </button>
      </div>
    </form>
  );
}

// ============ Activity Item ============

const ACTIVITY_ICONS = {
  call: Phone,
  meeting: MessageSquare,
  email: Mail,
  note: FileText,
};

const ACTIVITY_COLORS = {
  call: 'bg-blue-100 text-blue-600',
  meeting: 'bg-purple-100 text-purple-600',
  email: 'bg-green-100 text-green-600',
  note: 'bg-gray-100 text-gray-600',
};

function ActivityItem({ activity }: { activity: OpportunityActivity }) {
  const Icon = ACTIVITY_ICONS[activity.type];

  return (
    <div className="flex gap-3">
      <div className={cn(
        'flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0',
        ACTIVITY_COLORS[activity.type]
      )}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-700 leading-relaxed">{activity.description}</p>
        <p className="text-xs text-gray-400 mt-1">
          {new Date(activity.createdAt).toLocaleString()}
        </p>
      </div>
    </div>
  );
}

// ============ Edit Opportunity Modal ============

interface EditOpportunityModalProps {
  opportunity: {
    id: string;
    title: string;
    description: string | null;
    funding: string | null;
    fundingTypeId: string | null;
    serviceClass: string | null;
    accountDirectorId: string | null;
    accountDirectorName: string | null;
    scopingLeadId: string | null;
    scopingLeadName: string | null;
    deliveryLeadId: string | null;
    deliveryLeadName: string | null;
    salesOrg: string | null;
    contractStartDate: string | null;
    servicesExpiryDate: string | null;
    servicesValue: number | null;
    currency: 'GBP' | 'USD' | 'EUR';
    customerRelationshipStatus: RagStatus | null;
    customerRelationshipNotes: string | null;
    scopingStatus: RagStatus | null;
    scopingNotes: string | null;
    customerReadinessStatus: RagStatus | null;
    customerReadinessNotes: string | null;
    deliveryStatus: RagStatus | null;
    deliveryNotes: string | null;
    notes: string | null;
    nextStepsActions: string | null;
  };
  onClose: () => void;
  onSuccess: () => void;
}

function EditOpportunityModal({ opportunity, onClose, onSuccess }: EditOpportunityModalProps) {
  const updateOpportunity = useUpdateOpportunity();
  const { data: typesData } = useRateCardTypes();
  const [formData, setFormData] = useState({
    title: opportunity.title,
    description: opportunity.description || '',
    fundingTypeId: opportunity.fundingTypeId || '',
    serviceClass: opportunity.serviceClass || '',
    accountDirectorId: opportunity.accountDirectorId || null as string | null,
    accountDirectorName: opportunity.accountDirectorName || '',
    scopingLeadId: opportunity.scopingLeadId || null as string | null,
    scopingLeadName: opportunity.scopingLeadName || '',
    deliveryLeadId: opportunity.deliveryLeadId || null as string | null,
    deliveryLeadName: opportunity.deliveryLeadName || '',
    salesOrg: opportunity.salesOrg || '',
    contractStartDate: opportunity.contractStartDate || '',
    servicesExpiryDate: opportunity.servicesExpiryDate || '',
    servicesValue: opportunity.servicesValue ? (opportunity.servicesValue / 100).toString() : '',
    currency: opportunity.currency,
    customerRelationshipStatus: (opportunity.customerRelationshipStatus || 'na') as RagStatus,
    customerRelationshipNotes: opportunity.customerRelationshipNotes || '',
    scopingStatus: (opportunity.scopingStatus || 'na') as RagStatus,
    scopingNotes: opportunity.scopingNotes || '',
    customerReadinessStatus: (opportunity.customerReadinessStatus || 'na') as RagStatus,
    customerReadinessNotes: opportunity.customerReadinessNotes || '',
    deliveryStatus: (opportunity.deliveryStatus || 'na') as RagStatus,
    deliveryNotes: opportunity.deliveryNotes || '',
    notes: opportunity.notes || '',
    nextStepsActions: opportunity.nextStepsActions || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateOpportunity.mutateAsync({
      id: opportunity.id,
      title: formData.title,
      description: formData.description || null,
      fundingTypeId: formData.fundingTypeId || null,
      serviceClass: formData.serviceClass || null,
      accountDirectorId: formData.accountDirectorId,
      accountDirectorName: formData.accountDirectorName || null,
      scopingLeadId: formData.scopingLeadId,
      scopingLeadName: formData.scopingLeadName || null,
      deliveryLeadId: formData.deliveryLeadId,
      deliveryLeadName: formData.deliveryLeadName || null,
      salesOrg: formData.salesOrg || null,
      contractStartDate: formData.contractStartDate || null,
      servicesExpiryDate: formData.servicesExpiryDate || null,
      servicesValue: formData.servicesValue ? parseInt(formData.servicesValue, 10) * 100 : null,
      currency: formData.currency,
      customerRelationshipStatus: formData.customerRelationshipStatus,
      customerRelationshipNotes: formData.customerRelationshipNotes || null,
      scopingStatus: formData.scopingStatus,
      scopingNotes: formData.scopingNotes || null,
      customerReadinessStatus: formData.customerReadinessStatus,
      customerReadinessNotes: formData.customerReadinessNotes || null,
      deliveryStatus: formData.deliveryStatus,
      deliveryNotes: formData.deliveryNotes || null,
      notes: formData.notes || null,
      nextStepsActions: formData.nextStepsActions || null,
    });
    onSuccess();
    onClose();
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog max-w-2xl">
        <div className="dialog-header">
          <h2 className="dialog-title">Edit Opportunity</h2>
        </div>

        <form onSubmit={handleSubmit} className="dialog-body space-y-4">
          <div>
            <label className="label">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="input w-full mt-1"
            />
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="input w-full mt-1"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Services Value</label>
              <input
                type="number"
                value={formData.servicesValue}
                onChange={(e) => setFormData({ ...formData, servicesValue: e.target.value })}
                placeholder="0"
                className="input w-full mt-1"
              />
            </div>
            <div>
              <label className="label">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value as 'GBP' | 'USD' | 'EUR' })}
                className="input w-full mt-1"
              >
                <option value="GBP">GBP</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Funding</label>
              <select
                value={formData.fundingTypeId}
                onChange={(e) => setFormData({ ...formData, fundingTypeId: e.target.value })}
                className="input w-full mt-1"
              >
                <option value="">TBC</option>
                {typesData?.types?.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Service Class</label>
              <input
                type="text"
                value={formData.serviceClass}
                onChange={(e) => setFormData({ ...formData, serviceClass: e.target.value })}
                placeholder="e.g., T&M, Fixed Price"
                className="input w-full mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Account Director</label>
              <UserPicker
                userId={formData.accountDirectorId}
                userName={formData.accountDirectorName}
                onChange={({ userId, userName }) =>
                  setFormData({
                    ...formData,
                    accountDirectorId: userId,
                    accountDirectorName: userName || '',
                  })
                }
                placeholder="Pick or type name…"
                className="mt-1"
              />
            </div>
            <div>
              <label className="label">Sales Org</label>
              <input
                type="text"
                value={formData.salesOrg}
                onChange={(e) => setFormData({ ...formData, salesOrg: e.target.value })}
                className="input w-full mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Scoping Lead</label>
              <UserPicker
                userId={formData.scopingLeadId}
                userName={formData.scopingLeadName}
                onChange={({ userId, userName }) =>
                  setFormData({
                    ...formData,
                    scopingLeadId: userId,
                    scopingLeadName: userName || '',
                  })
                }
                placeholder="Pick or type name…"
                className="mt-1"
              />
            </div>
            <div>
              <label className="label">Delivery Lead</label>
              <UserPicker
                userId={formData.deliveryLeadId}
                userName={formData.deliveryLeadName}
                onChange={({ userId, userName }) =>
                  setFormData({
                    ...formData,
                    deliveryLeadId: userId,
                    deliveryLeadName: userName || '',
                  })
                }
                placeholder="Pick or type name…"
                className="mt-1"
              />
            </div>
          </div>

          {/* Health Status Section */}
          <div className="pt-4 border-t border-gray-200">
            <h4 className="font-medium text-sm text-xtravirt-dark mb-3">Health Status</h4>
            <div className="space-y-3">
              <RagFieldGroup
                label="Customer Relationship"
                status={formData.customerRelationshipStatus}
                notes={formData.customerRelationshipNotes}
                onStatusChange={(v) => setFormData({ ...formData, customerRelationshipStatus: v })}
                onNotesChange={(v) => setFormData({ ...formData, customerRelationshipNotes: v })}
              />
              <RagFieldGroup
                label="Scoping"
                status={formData.scopingStatus}
                notes={formData.scopingNotes}
                onStatusChange={(v) => setFormData({ ...formData, scopingStatus: v })}
                onNotesChange={(v) => setFormData({ ...formData, scopingNotes: v })}
              />
              <RagFieldGroup
                label="Customer Readiness"
                status={formData.customerReadinessStatus}
                notes={formData.customerReadinessNotes}
                onStatusChange={(v) => setFormData({ ...formData, customerReadinessStatus: v })}
                onNotesChange={(v) => setFormData({ ...formData, customerReadinessNotes: v })}
              />
              <RagFieldGroup
                label="Delivery"
                status={formData.deliveryStatus}
                notes={formData.deliveryNotes}
                onStatusChange={(v) => setFormData({ ...formData, deliveryStatus: v })}
                onNotesChange={(v) => setFormData({ ...formData, deliveryNotes: v })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Contract Start</label>
              <input
                type="date"
                value={formData.contractStartDate}
                onChange={(e) => setFormData({ ...formData, contractStartDate: e.target.value })}
                className="input w-full mt-1"
              />
            </div>
            <div>
              <label className="label">Services Expiry</label>
              <input
                type="date"
                value={formData.servicesExpiryDate}
                onChange={(e) => setFormData({ ...formData, servicesExpiryDate: e.target.value })}
                className="input w-full mt-1"
              />
            </div>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="input w-full mt-1"
              placeholder="Internal notes..."
            />
          </div>

          <div>
            <label className="label">Next Steps</label>
            <textarea
              value={formData.nextStepsActions}
              onChange={(e) => setFormData({ ...formData, nextStepsActions: e.target.value })}
              rows={3}
              className="input w-full mt-1"
              placeholder="Action items and next steps..."
            />
          </div>
        </form>

        <div className="dialog-footer">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formData.title || updateOpportunity.isPending}
            className="btn-primary"
          >
            {updateOpportunity.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ RAG Indicators ============

const RAG_COLORS: Record<RagStatus, { bg: string; text: string; dot: string; label: string }> = {
  green: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500', label: 'On Track' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', label: 'At Risk' },
  red: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500', label: 'Blocked' },
  na: { bg: 'bg-gray-50', text: 'text-gray-500', dot: 'bg-gray-300', label: 'Not Set' },
};

function RagDot({ status }: { status: RagStatus }) {
  const cfg = RAG_COLORS[status];
  return (
    <span
      className={cn('inline-block h-2.5 w-2.5 rounded-full flex-shrink-0', cfg.dot)}
      title={cfg.label}
    />
  );
}

function RagRow({ label, status, note }: { label: string; status: RagStatus; note: string | null }) {
  const cfg = RAG_COLORS[status];
  return (
    <div className="flex items-start gap-2">
      <span className={cn('inline-block h-3 w-3 rounded-full flex-shrink-0 mt-1', cfg.dot)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-gray-900">{label}</span>
          <span className={cn('text-xs px-2 py-0.5 rounded-full', cfg.bg, cfg.text)}>
            {cfg.label}
          </span>
        </div>
        {note && (
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{note}</p>
        )}
      </div>
    </div>
  );
}

interface RagFieldGroupProps {
  label: string;
  status: RagStatus;
  notes: string;
  onStatusChange: (v: RagStatus) => void;
  onNotesChange: (v: string) => void;
}

function RagFieldGroup({ label, status, notes, onStatusChange, onNotesChange }: RagFieldGroupProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-start">
      <div>
        <label className="label">{label}</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Notes / blocker reason"
          className="input w-full mt-1"
        />
      </div>
      <div>
        <label className="label">Status</label>
        <select
          value={status}
          onChange={(e) => onStatusChange(e.target.value as RagStatus)}
          className="input w-full mt-1 sm:w-32"
        >
          <option value="na">Not Set</option>
          <option value="green">Green</option>
          <option value="amber">Amber</option>
          <option value="red">Red</option>
        </select>
      </div>
    </div>
  );
}

// ============ Workstream Coverage ============

interface WorkstreamCoverageCardProps {
  coverage: Array<{ workstreamId: string; capabilityCount: number }>;
}

function WorkstreamCoverageCard({ coverage }: WorkstreamCoverageCardProps) {
  const [showNotScoped, setShowNotScoped] = useState(false);
  const { data: wsData } = useWorkstreams({ includeCapabilities: true });

  const workstreams = wsData?.workstreams || [];
  const coveredIds = new Set(coverage.map((c) => c.workstreamId));

  const covered = useMemo(() => {
    return workstreams
      .filter((ws) => coveredIds.has(ws.id))
      .map((ws) => {
        const match = coverage.find((c) => c.workstreamId === ws.id);
        return { ...ws, capabilityCount: match?.capabilityCount || 0 };
      });
  }, [workstreams, coverage]);

  const notScoped = useMemo(() => {
    return workstreams.filter((ws) => !coveredIds.has(ws.id));
  }, [workstreams, coveredIds]);

  return (
    <div className="card p-4 md:p-6">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="h-5 w-5 text-xtravirt-cyan" />
        <h3 className="font-heading text-base md:text-lg font-semibold text-xtravirt-dark">
          Workstream Coverage
        </h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        {covered.length} / {workstreams.length} workstreams covered
      </p>

      {covered.length > 0 && (
        <div className="space-y-1.5 mb-4">
          {covered.map((ws) => (
            <div key={ws.id} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                <span className="font-mono text-xs text-gray-500">{ws.code}</span>
                <span className="text-gray-700 truncate">{ws.name}</span>
              </div>
              <span className="text-xs text-gray-400 flex-shrink-0">
                {ws.capabilityCount} {ws.capabilityCount === 1 ? 'capability' : 'capabilities'}
              </span>
            </div>
          ))}
        </div>
      )}

      {notScoped.length > 0 && (
        <div className="pt-3 border-t border-gray-100">
          <button
            onClick={() => setShowNotScoped(!showNotScoped)}
            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            {showNotScoped ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            {notScoped.length} not scoped
          </button>
          {showNotScoped && (
            <div className="mt-2 space-y-1">
              {notScoped.map((ws) => (
                <div key={ws.id} className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="font-mono">{ws.code}</span>
                  <span>{ws.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============ Helpers ============

function formatCurrency(cents: number, currency: 'GBP' | 'USD' | 'EUR' = 'USD'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
